import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useQuery } from 'urql';
import { Period, SocialAdsAnalytics_QueryFragment } from '@frontend/gql/social/graphql';
import { insightsByClientId } from '../graphql';
import { TopAdData } from '../components/ads-table/ads-table-types';
import { SocialAccountData } from '../components/social-accounts-table/social-accounts-table-types';
import { filterAndSortAdsData, sortSocialAccountsData } from './use-social-ads-data-utils';

interface UseAdsQueryDataProps {
  clientId?: string;
  currentIntervalRange: {
    startDate: string;
    endDate: string;
    precision: Period;
  };
  previousIntervalRange: {
    startDate: string;
    endDate: string;
  };
  startDate: string;
  endDate: string;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignAndSetIds?: string[];
  adObjectiveIds?: string[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
  tab: string;
}

export const useAdsQueryData = ({
  clientId,
  currentIntervalRange,
  previousIntervalRange,
  startDate,
  endDate,
  fromOldData,
  adAccountIds,
  adCampaignAndSetIds,
  adObjectiveIds,
  memberIds: _memberIds,
  compareWithPartnershipAds: _compareWithPartnershipAds,
  tab,
}: UseAdsQueryDataProps) => {
  // Track previous values to detect changes
  const prevValues = useRef({
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignAndSetIds,
    adObjectiveIds,
    memberIds: _memberIds,
    compareWithPartnershipAds: _compareWithPartnershipAds,
  });

  const [refetching, setRefetching] = useState(false);

  // Execute the GraphQL query
  const [result, executeQuery] = useQuery({
    query: insightsByClientId,
    variables: {
      clientId: clientId || '',
      currentIntervalRange: {
        startDate: currentIntervalRange.startDate,
        endDate: currentIntervalRange.endDate,
        precision: currentIntervalRange.precision,
      },
      previousIntervalRange: {
        startDate: previousIntervalRange.startDate,
        endDate: previousIntervalRange.endDate,
      },
      fromOldData,
      adAccountIds: adAccountIds && adAccountIds.length > 0 ? adAccountIds : undefined,
    },
    pause: !clientId || tab !== 'overview',
  });

  const { data, fetching, error } = result;

  // Use data?.client directly instead of useFragment
  const client = (data as SocialAdsAnalytics_QueryFragment)?.client;

  // Check if parameters have changed
  const hasParamsChanged = useMemo(() => {
    const current = {
      clientId,
      currentIntervalRange: JSON.stringify(currentIntervalRange),
      previousIntervalRange: JSON.stringify(previousIntervalRange),
      startDate,
      endDate,
      fromOldData,
      adAccountIds: JSON.stringify(adAccountIds?.sort()),
      adCampaignAndSetIds: JSON.stringify(adCampaignAndSetIds?.sort()),
      adObjectiveIds: JSON.stringify(adObjectiveIds?.sort()),
      memberIds: JSON.stringify(_memberIds?.sort()),
      compareWithPartnershipAds: _compareWithPartnershipAds,
    };

    const previous = {
      clientId: prevValues.current.clientId,
      currentIntervalRange: JSON.stringify(prevValues.current.currentIntervalRange),
      previousIntervalRange: JSON.stringify(prevValues.current.previousIntervalRange),
      startDate: prevValues.current.startDate,
      endDate: prevValues.current.endDate,
      fromOldData: prevValues.current.fromOldData,
      adAccountIds: JSON.stringify(prevValues.current.adAccountIds?.sort()),
      adCampaignAndSetIds: JSON.stringify(prevValues.current.adCampaignAndSetIds?.sort()),
      adObjectiveIds: JSON.stringify(prevValues.current.adObjectiveIds?.sort()),
      memberIds: JSON.stringify(prevValues.current.memberIds?.sort()),
      compareWithPartnershipAds: prevValues.current.compareWithPartnershipAds,
    };

    return JSON.stringify(current) !== JSON.stringify(previous);
  }, [
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignAndSetIds,
    adObjectiveIds,
    _memberIds,
    _compareWithPartnershipAds,
  ]);

  // Update previous values when parameters change
  useEffect(() => {
    if (hasParamsChanged) {
      prevValues.current = {
        clientId,
        currentIntervalRange,
        previousIntervalRange,
        startDate,
        endDate,
        fromOldData,
        adAccountIds,
        adCampaignAndSetIds,
        adObjectiveIds,
        memberIds: _memberIds,
        compareWithPartnershipAds: _compareWithPartnershipAds,
      };
    }
  }, [
    hasParamsChanged,
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignAndSetIds,
    adObjectiveIds,
    _memberIds,
    _compareWithPartnershipAds,
  ]);

  // Manual refetch function with loading state
  const handleRefetch = useCallback(async () => {
    setRefetching(true);
    try {
      await executeQuery({ requestPolicy: 'network-only' });
    } finally {
      setRefetching(false);
    }
  }, [executeQuery]);

  // transform topAds to TopAdData[]
  const topAds: TopAdData[] = useMemo(() => {
    const adsData = client?.sumInsightsBy || [];
    const sortedAds = filterAndSortAdsData(adsData);
    return sortedAds.slice(0, 5); // Top 5 ads for overview
  }, [client]);

  // transform social accounts data to SocialAccountData[]
  const topSocialAccounts: SocialAccountData[] = useMemo(() => {
    const socialAccountsData = client?.sumInsightsBySocialAccounts || [];
    const sortedData = sortSocialAccountsData(socialAccountsData);
    return sortedData.slice(0, 5); // Top 5 social accounts for overview
  }, [client]);

  // Process the fragment data for easier consumption
  const processedData = useMemo(() => {
    // Use client directly
    return {
      metaAdAccounts: client?.metaAdAccounts || [],
      currentInterval: client?.currentInterval || null,
      currentIntervalsIntervalInsights: client?.currentIntervalsIntervalInsights || [],
      currentIntervalsIntervalInsightsNotAspireSourced: client?.currentIntervalsIntervalInsightsNotAspireSourced || [],
      previousInterval: client?.previousInterval || null,
      currentIntervalNotAspireSourced: client?.currentIntervalNotAspireSourced || null,
      previousIntervalNotAspireSourced: client?.previousIntervalNotAspireSourced || null,
      // Real data from GraphQL query - replacing mock data
      topAds,
      topSocialAccounts,
    };
  }, [client, topAds, topSocialAccounts]);

  return {
    fetching,
    refetching,
    error,
    processedData,
    handleRefetch,
  };
};
